import { Component, OnInit, ViewChild } from '@angular/core';
import { VTabComponent } from '@app/directives/_layout/v-tabs/v-tab.component';
import { VTabsComponent } from '@app/directives/_layout/v-tabs/v-tabs.component';
import { IPeripheral } from '@app/interfaces/IPeripheral';
import { CameraChannelsEditorComponent } from '@app/main/components/resourceManagement/cameras/camera-channels-editor/camera-channels-editor.component';
import { ProductVendor } from '@app/main/components/videos/enums/CameraProductType';
import { FoundationNavigationModule } from '@app/main/modules/foundation/foundation.navigation';
import { CameraLogsComponent } from '@app/main/modules/foundation/_routes/hardware/peripherals/camera-logs/camera-logs.component';
import { CameraApplicationRequestService } from '@app/main/modules/foundation/_routes/rv-lite/application/_services/cameraApplicationRequestService';
import { IApplicationCameraRequest, IConfigCameraRequest } from '@app/main/modules/foundation/_routes/rv-lite/camera-config/camera-configuration.models';
import { CameraConfigurationRequestService } from '@app/main/modules/foundation/_routes/rv-lite/config/_services/cameraConfigurationRequestService';
import { PeripheralService } from '@app/main/modules/foundation/_services/peripheral.service';
import { CameraCommunicationServerService } from '@app/services/camera/camera-communication-server.service';
import { IConfigurationUpdate } from '@app/services/cameraCommunicationServer.service';
import { NavigationService } from '@app/services/navigation.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, forkJoin, Observable } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { IFtCloudDeviceMetadataInfo } from '../../_models/IFtCloudDeviceMetadataPayload';
import { IPeripheralMetadata } from '../../_models/IPeripheralMetadata';
import { CameraVideosComponent } from '../camera-videos/camera-videos.component';
import './camera-view.component.scss';

@UntilDestroy()
@Component({
    selector: 'camera-view',
    template: require('./camera-view.component.html')
})
export class CameraViewComponent implements OnInit {
    cameraId: number;
    cameraSerial: string;
    camera: IPeripheral;

    deviceTypeId: number;
    deviceId: number;
    isAutoDeployment: boolean;
    currentConfigId: number;
    configHistoryData: IConfigurationUpdate[] = [];
    peripheralMetadata: IPeripheralMetadata;
    streamaxMetadata: IFtCloudDeviceMetadataInfo;

    selectedTab: VTabComponent;

    loading: boolean;
    saving: boolean;

    @ViewChild("channelsTab") channelsTab: VTabComponent;
    @ViewChild("cameraChannelsEditor") cameraChannelsEditor: CameraChannelsEditorComponent;

    @ViewChild("logsTab") logsTab: VTabComponent;
    @ViewChild("cameraLogsViewer") cameraLogsViewer: CameraLogsComponent;

    @ViewChild("cameraVideos") cameraVideos: CameraVideosComponent;

    @ViewChild(VTabsComponent) tabs: VTabsComponent;
    
       constructor(
        private readonly cameraApplicationRequestService: CameraApplicationRequestService,
        private readonly cameraConfigurationRequestService: CameraConfigurationRequestService,
        private readonly cameraCommunicationServer: CameraCommunicationServerService,
        private readonly navigationService: NavigationService,
        private readonly peripheralService: PeripheralService) { }

    ngOnInit(): void {
        this.cameraSerial = this.navigationService.getRouteParam("serial");
        this.loadData();
    }

    onSelectedTabChanged(tab: VTabComponent): void {        
        this.selectedTab = tab;
    }

    saveChannels($event): void {
        this.saving = true;
        this.cameraChannelsEditor.saveChannels();
    }

    createLogRequest(): void {
        this.cameraLogsViewer.toggleCreateRequestSidebar();
    }

    onChannelsSaved(): void {
        this.navigationService.go(FoundationNavigationModule.Cameras, null, false);
    }

    selectChannelsTab(): void {
        this.tabs.selectTab(this.channelsTab);
    }

    requestVideo(): void {
        this.cameraVideos.onRequestVideo();
    }

    get channelsTabSelected(): boolean {
        return this.selectedTab?.tabId == "channels"
    }

    get logsTabSelected(): boolean {
        return this.selectedTab?.tabId == "logs";
    }

    get videosTabSelected(): boolean {
        return this.selectedTab?.tabId == "videos";
    }

    get hidePulseMessageTab(): boolean {
        return (!this.camera?.Product?.IsCameraProduct) || !(this.camera?.Product?.GroupId === ProductVendor.RTL);
    }

    get hidePeripheralJsonTab(): boolean {
        return (this.camera?.Product?.GroupId === ProductVendor.Streamax)
    }

    get isP100(): boolean {
        return this.camera?.Product?.GroupId === ProductVendor.RTL
    }

    //--

    private loadData(): void {
        this.loading = true;
        combineLatest([
            this.getPeripheral(),
            this.getVeriConfigData(),
            this.getVeriAppData(),
            this.getPeripheralMetadata()
        ]).pipe(
            untilDestroyed(this))
            .subscribe({
                next: ([peripheral, veriConfigData, veriAppData, metaData]) => {
                    this.camera.veriAppUpdateHistory = veriAppData;
                    this.camera.veriConfigUpdateHistory = veriConfigData;
                    this.loading = false;
                }
            })
    }

    private getPeripheralMetadata(): Observable<IPeripheralMetadata> {
        return this.peripheralService.getPeripheralMetadata(this.cameraSerial).pipe(untilDestroyed(this), tap((metadata) => {
            this.peripheralMetadata = metadata;
            this.streamaxMetadata = !!metadata?.DeviceMetadataInfoJson?.length ? JSON.parse(metadata.DeviceMetadataInfoJson) as IFtCloudDeviceMetadataInfo : null;
        }));
    }

    private getPeripheral(): Observable<IPeripheral> {
        return this.peripheralService.getPeripheralBySerial(this.cameraSerial).pipe(map(peripheral => {
            this.camera = peripheral;

            if (!this.camera) {
                return;
            }

            this.navigationService.updateBreadcrumbNameResolver({
                peripheralNameBreadcrumbKey: this.camera?.name ?? "Unknown",
            });

            return peripheral;
        }));
    }

    private getVeriAppData(): Observable<IApplicationCameraRequest[]> {
        return this.cameraApplicationRequestService.getApplicationRequests({ Serial: this.cameraSerial });
    }

    private getVeriConfigData(): Observable<IConfigCameraRequest[]> {
        return this.cameraConfigurationRequestService.getConfigurationRequests({ Serial: this.cameraSerial });
    }

    private getRtlConfigData(): Observable<IConfigurationUpdate[]> {
        return forkJoin({
            deviceInfo: this.cameraCommunicationServer.getDeviceInfo(this.cameraSerial),
        })
            .pipe(mergeMap(data => {
                this.deviceTypeId = data.deviceInfo?.DeviceTypeId;
                this.deviceId = data.deviceInfo?.DeviceId;
                this.isAutoDeployment = data.deviceInfo?.IsAutoDeployment;
                this.currentConfigId = data.deviceInfo?.ConfigId;
                var t = this.cameraApplicationRequestService.getApplicationRequests({ Serial: this.cameraSerial });
                return this.cameraCommunicationServer.getConfigurationUpdateHistory(this.deviceId);
            }));
    }
}
